import { z } from "zod";
import { optionalUuid } from "./_helpers/optional";
import { csvUuids } from "./common.schema";
import { ExternalIdEntrySchema } from "./external-id.schema";

export const opportunityStages = [
  "lead",
  "appointment",
  "proposal",
  "closed_won",
  "closed_lost",
] as const;

export const opportunityTypes = [
  "project",
  "service_call",
  "service_agreement",
  "order",
  "warranty",
  "refresh",
  "amendment_change_order",
] as const;

export const OpportunitySchema = z.object({
  id: z.string().uuid().describe("Opportunity"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  brand_id: z.string().uuid().nullable().optional().describe("Brand"),
  property_id: z.string().uuid().nullable().optional().describe("Property"),
  customer_role_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Customer"),
  assigned_rep_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Assigned Rep"),
  deal_number: z.string().nullable().optional().describe("Opportunity Number"),
  name: z.string().nullable().optional().describe("Name"),
  deal_type: z.enum(opportunityTypes).describe("Opportunity Type"),
  stage: z.enum(opportunityStages).describe("Stage"),
  sub_status: z.string().nullable().optional().describe("Sub-Status"),
  lead_date: z.string().nullable().optional().describe("Lead Date"),
  sold_date: z.string().nullable().optional().describe("Sold Date"),
  completed_date: z.string().nullable().optional().describe("Completed Date"),
  lead_source: z.string().nullable().optional().describe("Lead Source"),
  is_self_gen: z.boolean().nullable().optional().describe("Self-Generated"),
  description: z.string().nullable().optional().describe("Description"),
  notes: z.string().nullable().optional().describe("Notes"),
  specifications: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Specifications"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  stage_entered_at: z
    .string()
    .nullable()
    .optional()
    .describe("Stage Entered At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});

export type Opportunity = z.infer<typeof OpportunitySchema>;

export const OpportunityInsertSchema = z.object({
  brand_id: optionalUuid().describe("Brand"),
  property_id: optionalUuid().describe("Property"),
  customer_role_id: optionalUuid().describe("Customer"),
  assigned_rep_id: optionalUuid().describe("Assigned Rep"),
  deal_number: z.string().optional().describe("Opportunity Number"),
  name: z.string().optional().describe("Name"),
  deal_type: z
    .enum(opportunityTypes)
    .default("project")
    .describe("Opportunity Type"),
  stage: z.enum(opportunityStages).default("lead").describe("Stage"),
  sub_status: z.string().optional().describe("Sub-Status"),
  lead_date: z.string().optional().describe("Lead Date"),
  sold_date: z.string().optional().describe("Sold Date"),
  completed_date: z.string().optional().describe("Completed Date"),
  lead_source: z.string().optional().describe("Lead Source"),
  is_self_gen: z.boolean().optional().describe("Self-Generated"),
  description: z.string().optional().describe("Description"),
  notes: z.string().optional().describe("Notes"),
  specifications: z.record(z.unknown()).optional().describe("Specifications"),
  custom_fields: z.record(z.unknown()).optional().describe("Custom Fields"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
});

export const OpportunityUpdateSchema = OpportunityInsertSchema.partial();

export const OpportunityQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  customer_role_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  stage: z.enum(opportunityStages).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  deal_type: z.enum(opportunityTypes).optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  search: z.string().optional(),
  // ADV-1362: multi-select salesperson facet. Filters on `assigned_rep_id`
  // (a contact_role id, 100% populated + name-resolved in the view) per
  // spec D11 — not `salesperson_user_id` (auth.users, 32%, CASL-scoped).
  // CSV string → uuid[] via the shared helper; server-side `.in()`.
  assigned_rep_id: csvUuids().optional(),
});

export const OpportunityStageConfigSchema = z.object({
  id: z.string().uuid().describe("Stage Config"),
  business_id: z.string().uuid().describe("Business"),
  deal_type: z.enum(opportunityTypes).describe("Opportunity Type"),
  stage: z.enum(opportunityStages).describe("Stage"),
  sub_statuses: z.array(z.string()).describe("Sub-Statuses"),
  sort_order: z.number().describe("Sort Order"),
  is_active: z.boolean().describe("Active"),
  config: z.record(z.unknown()).nullable().optional().describe("Config"),
});

export type OpportunityStageConfig = z.infer<
  typeof OpportunityStageConfigSchema
>;
